import { ArrowDownIcon, ArrowUpIcon, Select } from '@blueprinthq/joy'
import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Stack,
  Text,
  Textarea,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react'
import Memories from '@handlers/sessions/components/progress-note-template-memory/memories'
import { useExperienceManager } from '@hooks'
import { TrashIcon } from '@icons'
import {
  FieldArray,
  Form,
  Formik,
  type FormikErrors,
  type FormikTouched
} from 'formik'
import React, { useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import * as Yup from 'yup'
import { useProgressNoteTemplateMemoryControllerFindByNoteType } from '~/clinician-api'
import DeleteTemplateDialog from './delete-template-dialog'

// Shared maps for consistency across components
export const SessionTypeMap: Record<string, string> = {
  individual: 'Individual',
  couple: 'Couple (coming soon)'
}

export const SpecializationMap: Record<string, string> = {
  therapists: 'Therapy',
  prescribers: 'Prescriber'
}

const SubtractIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12.6666 8.66665H3.33325V7.33331H12.6666V8.66665Z"
      fill="#282828"
    />
  </svg>
)

const PlusIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8.66659 12.6667V8.66669H12.6666V7.33335H8.66659V3.33335H7.33325V7.33335H3.33325V8.66669H7.33325V12.6667H8.66659Z"
      fill="#282828"
    />
  </svg>
)

const FormActions = ({
  readOnly,
  submitButtonText,
  onCancel,
  isSubmitting,
  isMobile,
  onDelete,
  showDeleteButton = false
}: {
  readOnly: boolean
  submitButtonText: string
  onCancel: () => void
  isSubmitting: boolean
  isMobile: boolean
  onDelete?: () => void
  showDeleteButton?: boolean
}) => {
  return (
    <HStack
      spacing="0px"
      gap="8px"
      justifyContent="flex-end"
      alignItems={{ base: 'center', md: 'flex-start' }}
    >
      {showDeleteButton && !readOnly && (
        <IconButton
          m="0px"
          w="32px"
          variant="outline"
          type="button"
          onClick={onDelete}
          aria-label="Delete template"
          borderColor="pale_gray"
          p="0"
          _hover={{ boxShadow: 'inset 0 0 100px 100px rgba(0, 0, 0, 0.05)' }}
          icon={
            <TrashIcon
              width="16px"
              height="16px"
              fill="rgba(235, 81, 100, 1)"
            />
          }
        />
      )}
      <Button
        m="0px"
        w={isMobile ? '100%' : '100px'}
        variant="outline"
        type="button"
        _hover={{
          boxShadow: 'inset 0 0 100px 100px rgba(0, 0, 0, 0.05)'
        }}
        borderColor="pale_gray"
        onClick={onCancel}
      >
        Cancel
      </Button>
      {!readOnly && (
        <Button
          m="0px"
          w={isMobile ? '100%' : '155px'}
          colorScheme="primary"
          type="submit"
          isLoading={isSubmitting}
        >
          {submitButtonText}
        </Button>
      )}
    </HStack>
  )
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Template name is required'),
  sections: Yup.array()
    .of(
      Yup.object().shape({
        name: Yup.string().required('Section name is required'),
        sampleOutputs: Yup.array().of(
          Yup.string().required('Sample output is required')
        )
      })
    )
    .min(1, 'At least one section is required')
})

export interface SectionType {
  name: string
  id: string
  sampleOutputs: string[]
  instructions?: string
}

export interface FormValues {
  name: string
  sections: SectionType[]
  sessionType: string
  specialization: string
}

export interface ProgressNoteTemplateFormProps {
  readOnly?: boolean
  template?: any
  initialValues: FormValues
  onSubmit: (values: FormValues) => void
  onCancel: () => void
  onDelete?: () => void
  submitButtonText?: string
  isDeleting?: boolean
}

const TextAreaAutoHeight = (props: any) => {
  const ref = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    const element = ref.current
    if (element && element.value.length > 0) {
      element.style.height = 'auto'
      element.style.height = `${element.scrollHeight}px`
    }
  }, [ref.current])

  useEffect(() => {
    const element = ref.current
    if (element) {
      element.style.height = 'auto'
      element.style.height = `${element.scrollHeight}px`
    }
  }, [props.value])

  return (
    <Textarea
      ref={ref}
      resize="none"
      overflowY="hidden"
      minH="80px"
      _placeholder={{
        color: 'dark_gray'
      }}
      {...props}
    />
  )
}

export const ProgressNoteTemplateForm: React.FC<
  ProgressNoteTemplateFormProps
> = ({
  readOnly,
  template,
  initialValues,
  onSubmit,
  onCancel,
  onDelete,
  submitButtonText = 'Save template',
  isDeleting
}) => {
  const isMobile = useBreakpointValue({
    base: true,
    md: false
  })
  const history = useHistory()
  const [minimizedSections, setMinimizedSections] = React.useState<Set<string>>(
    () => {
      if (template && initialValues.sections.length > 1) {
        const minimized = new Set<string>()
        initialValues.sections.forEach((section, index) => {
          if (index > 0) {
            minimized.add(section.id)
          }
        })
        return minimized
      }
      return new Set()
    }
  )
  const { showCustomTemplateInstructions, memoryEnabled } =
    useExperienceManager()

  const {
    isOpen: isDeleteDialogOpen,
    onOpen: onOpenDeleteDialog,
    onClose: onCloseDeleteDialog
  } = useDisclosure()

  const {
    data: memories = [],
    isLoading: isMemoryLoading,
    refetch
  } = useProgressNoteTemplateMemoryControllerFindByNoteType(
    {
      noteType: template?.noteType
    },
    {
      query: {
        enabled: !!template?.noteType
      }
    }
  )

  const handleDelete = () => {
    if (onDelete) {
      onDelete()
    }
  }

  const memoryRef = useRef<HTMLDivElement>(null)

  return (
    <>
      <DeleteTemplateDialog
        isOpen={isDeleteDialogOpen}
        onClose={onCloseDeleteDialog}
        onDelete={handleDelete}
        isDeleting={isDeleting}
      />

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleSubmit,
          setFieldValue,
          isSubmitting
        }) => {
          const formErrors = errors as FormikErrors<FormValues>
          const formTouched = touched as FormikTouched<FormValues>

          const isSectionInvalid = (index: number) =>
            // @ts-ignore
            !!formTouched.sections?.[index]?.sampleOutputs?.[0] &&
            // @ts-ignore
            !!formErrors.sections?.[index]?.sampleOutputs?.[0]

          return (
            <Form style={{ width: '100%' }}>
              <Box h="8px" />
              <Flex flexDirection="column" gap="16px">
                <Flex justifyContent="space-between" alignItems="center">
                  <Text
                    as="h3"
                    alignItems="center"
                    fontSize="24px"
                    lineHeight="100%"
                    fontWeight="bold"
                  >
                    {template ? 'Edit Template' : 'New Template'}
                  </Text>
                  {isMobile && template && (
                    <Button
                      textDecoration="none"
                      variant="link"
                      m="0px"
                      colorScheme="primary"
                      _focus={{ outline: 'none' }}
                      _hover={{ textDecoration: 'underline' }}
                      onClick={() => {
                        memoryRef.current?.scrollIntoView({
                          behavior: 'smooth'
                        })
                      }}
                      rightIcon={<ArrowDownIcon fill="#2D54E8" />}
                      iconSpacing="4px"
                    >
                      Jump to Memory
                    </Button>
                  )}
                  {!isMobile && (
                    <FormActions
                      readOnly={!!readOnly}
                      submitButtonText={submitButtonText}
                      onCancel={onCancel}
                      isSubmitting={isSubmitting}
                      isMobile={!!isMobile}
                      onDelete={onOpenDeleteDialog}
                      showDeleteButton={!!template}
                    />
                  )}
                </Flex>
              </Flex>
              <Box h={{ base: '16px', md: '24px' }} />
              <Flex
                flex="1"
                gap="32px"
                position="relative"
                flexDirection={{ base: 'column', md: 'row' }}
              >
                <Stack gap={{ base: '16px' }} spacing="0px" flex="1">
                  <Stack spacing="0" gap={{ base: '16px', md: '24px' }}>
                    <Flex
                      flexDirection={{ base: 'column', md: 'row' }}
                      gap={{ base: '16px' }}
                      justifyContent="space-between"
                    >
                      <FormControl
                        isInvalid={Boolean(formTouched.name && formErrors.name)}
                        flex="1"
                      >
                        <Input
                          placeholder="Add template name"
                          name="name"
                          borderColor="pale_gray"
                          onChange={handleChange}
                          value={values.name}
                          width="100%"
                          fontWeight="bold"
                          readOnly={readOnly}
                          _placeholder={{
                            color: 'dark_gray',
                            fontWeight: 'normal'
                          }}
                        />
                        <FormErrorMessage>
                          Template name is required
                        </FormErrorMessage>
                      </FormControl>
                      <Flex flex="1" gap="16px" maxW={{ md: '315px' }}>
                        <FormControl flex="1">
                          <Select
                            name="sessionType"
                            label=""
                            options={Object.entries(SessionTypeMap).map(
                              ([value, label]) => ({
                                value,
                                label
                              })
                            )}
                            valueKey="value"
                            labelKey="label"
                            style={{ borderColor: '#E4E5E6' }}
                            _focus={{ outline: 'none' }}
                            ref={el => {
                              setTimeout(() => {
                                const menuItems = document.querySelectorAll(
                                  'button[role="menuitemradio"]'
                                )
                                menuItems.forEach(item => {
                                  if (item?.textContent?.includes('Couple')) {
                                    if (item) {
                                      ;(item as HTMLButtonElement).disabled =
                                        true
                                      ;(
                                        item as HTMLButtonElement
                                      ).style.opacity = '0.5'
                                      ;(
                                        item as HTMLButtonElement
                                      ).style.cursor = 'not-allowed'
                                    }
                                  }
                                })
                              }, 100)
                            }}
                            value={{
                              value: values.sessionType || 'individual',
                              label:
                                SessionTypeMap[values.sessionType] ||
                                SessionTypeMap['individual']
                            }}
                            onChange={option => {
                              if (option.value !== 'couple') {
                                setFieldValue('sessionType', option.value)
                              }
                            }}
                          />
                        </FormControl>
                        <FormControl flex="1">
                          <Select
                            name="specialization"
                            label=""
                            options={Object.entries(SpecializationMap).map(
                              ([value, label]) => ({ value, label })
                            )}
                            valueKey="value"
                            labelKey="label"
                            _focus={{ outline: 'none' }}
                            style={{ borderColor: '#E4E5E6' }}
                            value={{
                              value: values.specialization || 'therapists',
                              label:
                                SpecializationMap[values.specialization] ||
                                SpecializationMap['therapists']
                            }}
                            onChange={option =>
                              setFieldValue('specialization', option.value)
                            }
                          />
                        </FormControl>
                      </Flex>
                    </Flex>
                    <Stack flex="1" gap="16px" spacing="0px">
                      <FieldArray name="sections">
                        {({ push, remove, move }) => (
                          <>
                            {values.sections.map((section, index) => (
                              <Stack
                                key={section.id}
                                spacing="0px"
                                border="1px solid"
                                borderColor="pale_gray"
                                borderRadius="8px"
                                bg="rgba(249, 249, 249, 1)"
                                p="16px"
                                gap="16px"
                              >
                                <HStack
                                  spacing="0"
                                  justifyContent="space-between"
                                  flex="1"
                                >
                                  <Text
                                    fontWeight={
                                      minimizedSections.has(section.id)
                                        ? 'bold'
                                        : 'normal'
                                    }
                                  >
                                    {minimizedSections.has(section.id)
                                      ? section.name || 'Add section name'
                                      : 'Section'}
                                  </Text>
                                  <Button
                                    variant="ghost"
                                    p="0"
                                    minW="0"
                                    height="auto"
                                    onClick={() => {
                                      setMinimizedSections(prev => {
                                        const next = new Set(prev)
                                        if (next.has(section.id)) {
                                          next.delete(section.id)
                                        } else {
                                          next.add(section.id)
                                        }
                                        return next
                                      })
                                    }}
                                    _focus={{
                                      outline: 'none'
                                    }}
                                  >
                                    {minimizedSections.has(section.id) ? (
                                      <PlusIcon />
                                    ) : (
                                      <SubtractIcon />
                                    )}
                                  </Button>
                                </HStack>
                                {!minimizedSections.has(section.id) && (
                                  <>
                                    <FormControl
                                      isInvalid={Boolean(
                                        // @ts-ignore
                                        formTouched.sections?.[index]?.name &&
                                          // @ts-ignore
                                          formErrors.sections?.[index]?.name
                                      )}
                                    >
                                      <Input
                                        bg="white"
                                        placeholder="Add section name"
                                        name={`sections.${index}.name`}
                                        onChange={handleChange}
                                        value={section.name}
                                        readOnly={readOnly}
                                        fontWeight="bold"
                                        borderColor="pale_gray"
                                        _placeholder={{
                                          color: 'dark_gray',
                                          fontWeight: 'normal'
                                        }}
                                      />
                                      <FormErrorMessage>
                                        Section name is required
                                      </FormErrorMessage>
                                    </FormControl>
                                    {section.instructions &&
                                      showCustomTemplateInstructions && (
                                        <>
                                          <Text fontWeight="bold">
                                            Instructions
                                          </Text>
                                          <Box
                                            bg="white"
                                            p="12px"
                                            borderRadius="8px"
                                            border="1px solid"
                                            borderColor="light_gray"
                                          >
                                            <Text
                                              fontSize="sm"
                                              whiteSpace="pre-wrap"
                                            >
                                              {section.instructions}
                                            </Text>
                                          </Box>
                                        </>
                                      )}
                                    <FormControl
                                      isInvalid={isSectionInvalid(index)}
                                    >
                                      <Stack
                                        border="1px solid"
                                        borderColor="pale_gray"
                                        borderRadius="8px"
                                        bg="white"
                                        spacing="0"
                                      >
                                        <Text p="16px">Sample output</Text>
                                        <Box px="16px">
                                          <Divider borderColor="pale_gray" />
                                        </Box>
                                        <TextAreaAutoHeight
                                          value={section.sampleOutputs[0]}
                                          onChange={handleChange}
                                          name={`sections.${index}.sampleOutputs[0]`}
                                          placeholder="Provide an example note for the AI to follow for this section"
                                          bg="white"
                                          border="none"
                                          p="16px"
                                          _focus={{ boxShadow: 'none' }}
                                          readOnly={readOnly}
                                        />
                                      </Stack>
                                      <FormErrorMessage>
                                        Sample output is required
                                      </FormErrorMessage>
                                    </FormControl>
                                  </>
                                )}
                                {!minimizedSections.has(section.id) && (
                                  <HStack justifyContent="space-between">
                                    {!readOnly && (
                                      <HStack>
                                        <Button
                                          variant="ghost"
                                          p="0"
                                          minW="0"
                                          height="auto"
                                          onClick={() =>
                                            index > 0 && move(index, index - 1)
                                          }
                                          _focus={{ outline: 'none' }}
                                          isDisabled={
                                            index === 0 ||
                                            values.sections.length <= 1
                                          }
                                        >
                                          <ArrowUpIcon />
                                        </Button>
                                        <Button
                                          variant="ghost"
                                          p="0"
                                          minW="0"
                                          height="auto"
                                          onClick={() =>
                                            index <
                                              values.sections.length - 1 &&
                                            move(index, index + 1)
                                          }
                                          _focus={{ outline: 'none' }}
                                          isDisabled={
                                            index ===
                                              values.sections.length - 1 ||
                                            values.sections.length <= 1
                                          }
                                        >
                                          <ArrowDownIcon />
                                        </Button>
                                      </HStack>
                                    )}
                                    {!readOnly && (
                                      <Button
                                        variant="ghost"
                                        p="0"
                                        minW="0"
                                        height="auto"
                                        onClick={() => {
                                          if (values.sections.length > 1) {
                                            remove(index)
                                          }
                                        }}
                                        _focus={{ outline: 'none' }}
                                      >
                                        <TrashIcon fill="rgba(235, 81, 100, 1)" />
                                      </Button>
                                    )}
                                  </HStack>
                                )}
                              </Stack>
                            ))}
                            {!readOnly && (
                              <Button
                                m="0"
                                colorScheme="primary"
                                onClick={() =>
                                  push({
                                    name: '',
                                    id: crypto.randomUUID(),
                                    sampleOutputs: ['']
                                  })
                                }
                                maxW={{
                                  base: '162px'
                                }}
                              >
                                + Add section
                              </Button>
                            )}
                          </>
                        )}
                      </FieldArray>
                    </Stack>
                  </Stack>
                </Stack>
                <Box
                  w={{ base: '100%', md: '352px' }}
                  position={{ base: 'relative', md: 'sticky' }}
                  top={{ md: '32px' }}
                  height="fit-content"
                  alignSelf="flex-start"
                >
                  <Flex flexDirection="column" gap="32px">
                    {memoryEnabled && (
                      <Stack alignItems="flex-start">
                        {template && memories.length ? (
                          <Stack
                            w="100%"
                            spacing="0"
                            border="1px solid"
                            borderColor="pale_gray"
                            borderRadius="8px"
                            bg="rgba(249, 249, 249, 1)"
                            p="16px"
                            gap="16px"
                            ref={memoryRef}
                          >
                            <Text fontWeight="bold">Memory</Text>
                            <Memories
                              noteTypeLabel={template?.name}
                              noteType={template.noteType}
                              p="0px"
                              bg="white"
                            />
                          </Stack>
                        ) : (
                          <Stack
                            w="100%"
                            spacing="0"
                            border="1px solid"
                            borderColor="pale_gray"
                            borderRadius="8px"
                            bg="rgba(249, 249, 249, 1)"
                            p="16px"
                            gap="16px"
                            ref={memoryRef}
                          >
                            <Text fontWeight="bold">Memory</Text>
                            <Text color="dark_gray" textAlign="center">
                              We'll learn your style and preferences as you use
                              this template.
                            </Text>
                          </Stack>
                        )}
                      </Stack>
                    )}
                  </Flex>
                </Box>
              </Flex>
              <Box h={{ md: '64px' }} />
              {isMobile && <Box mb="112px" />}
              {isMobile && (
                <Box
                  position="fixed"
                  bottom="0"
                  left="0"
                  right="0"
                  bg="white"
                  p="16px"
                  zIndex="1000"
                  borderTop="1px solid"
                  borderColor="pale_gray"
                >
                  <FormActions
                    readOnly={!!readOnly}
                    submitButtonText={submitButtonText}
                    onCancel={onCancel}
                    isSubmitting={isSubmitting}
                    isMobile={!!isMobile}
                    onDelete={onOpenDeleteDialog}
                    showDeleteButton={!!template}
                  />
                </Box>
              )}
            </Form>
          )
        }}
      </Formik>
    </>
  )
}

export default ProgressNoteTemplateForm
